import React, { useEffect } from "react";
import { Link, useLocation } from "react-router";

import { BottomNavigation, BottomNavigationAction } from "@mui/material";
import { BNGoogleTagManagerCollection, getRouteAction, useStatePersisted } from "../../Helpers/helpers";
import BNBox from "../BNBox/BNBox";
import BNHorizontalLoader from "../BNHeader/BNHorizontalLoader";
import { useTheme } from "@mui/material/styles";
import { RouteNameToMuiIconMapper } from "../../Helpers/IconSrcToMuiMapper/RouteNameToMuiIconMapper";
import { UiElementClickEvent } from "../../Helpers/GoogleTagManager/UiElementClickEvent";

const { BNGoogleTagManager, EventAction } = BNGoogleTagManagerCollection;

interface Action {
  title: string;
  url: string;
  svgIconSrc?: string;
  routeName?: string;
}

export interface BNBottomNavigationProps {
  isSpa: (path: string) => boolean;
  isLoading?: boolean;
  isMyPageRoot?: boolean;
  shouldBeActive?: (currentPath: string, actionUrl: string, productName: string) => boolean;
  currentProductName?: "creditcard" | "loan" | "savingsaccount" | "insurance";
  actionList?: Action[];
  visibleOnAllWidths: boolean;
}

export const BNBottomNavigation: React.FC<BNBottomNavigationProps> = ({
  isSpa = () => false,
  isLoading,
  isMyPageRoot,
  currentProductName,
  shouldBeActive,
  actionList: actionListFromProps,
  visibleOnAllWidths: isApp,
}) => {
  const theme = useTheme();
  const { pathname } = useLocation();

  const [actionList, setActionList] = useStatePersisted<Action[]>(
    null,
    "BOTTOM_NAVIGATION_ACTION_LIST",
    actionListFromProps || [],
  );

  useEffect(() => {
    if (actionListFromProps?.length) {
      setActionList(actionListFromProps);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetch(`/api/navigation/bottomnavigation?path=${pathname}`)
        .then((r) => r.json())
        .then((result) => setActionList(result.bottomNavigationLinks));
    }
  }, [actionListFromProps]);

  const homeUrl = actionList[0]?.url;
  const isRoot = isMyPageRoot === true;

  const productName = (actionUrl: string) => {
    const segments = actionUrl.split("/");
    const result = segments[1] === "sv" ? segments[3] || "" : segments[2] || "";
    return result.toLowerCase();
  };

  const currentAction = actionList.find((link) => {
    if (isRoot) {
      return homeUrl === link.url;
    }
    // Skip home when not root as it's always part of the slug
    if (homeUrl === link.url) {
      return false;
    }

    if (currentProductName && currentProductName === productName(link.url)) {
      return true;
    }

    if (shouldBeActive && shouldBeActive(pathname, link.url, productName(link.url))) {
      return true;
    }

    return !isRoot && pathname.startsWith(link.url);
  });

  const LinkProps = (url) => (isSpa(url) ? { to: url, component: Link } : { href: url });

  return (
    <BNBox
      sx={[
        {
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: "6rem",
          backgroundColor: "background.default",
          borderTop: `1px solid ${theme.vars.palette.secondary.light}`,
          lineHeight: "unset",
          zIndex: 1200, //One more than .PrivateSwipeArea that is for Opening BNDrawer when it's closed
          display: isApp ? undefined : { md: "none" },
          "@media print": {
            display: "none",
          },
        },
        theme.applyStyles("dark", {
          borderTop: `1px solid color-mix(in srgb, ${theme.vars.palette.shadow.white} 20%, transparent)`,
        }),
      ]}
    >
      <BNHorizontalLoader isLoading={isLoading === true} bottom="6rem" />
      <BottomNavigation
        value={currentAction}
        showLabels
        data-uit-container="bottom-navigation"
        sx={{
          backgroundColor: "background.default",
          height: "6rem",
        }}
      >
        {actionList.map((action) => (
          <BottomNavigationAction
            key={`bottom-navigation-action-${action.svgIconSrc}`}
            onClick={() =>
              BNGoogleTagManager.addEventToDataLayer(
                new UiElementClickEvent(EventAction.BottomNavigation, action.title),
              )
            }
            label={action.title}
            icon={
              <RouteNameToMuiIconMapper
                routeName={action.routeName}
                consumer={`component=BottomNavigation title=${action.title}`}
                iconSx={{ fontSize: "3rem" }}
              />
            }
            data-uit={getRouteAction(action.url)}
            value={action}
            sx={[
              {
                color: "tertiary.dark",
                justifyContent: "start",
                paddingTop: 0.5,
                ["& .MuiSvgIcon-root"]: {
                  opacity: 0.6,
                },
                ["&.Mui-selected .MuiSvgIcon-root"]: {
                  opacity: 1,
                },
                ["& .MuiBottomNavigationAction-label"]: {
                  fontSize: "1.2rem",
                  ["&.Mui-selected"]: {
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                  },
                },
                ["&.Mui-selected"]: {
                  color: "tertiary.dark",
                  [".MuiSvgIcon-root"]: {
                    opacity: 1,
                  },
                },
              },
              theme.applyStyles("dark", {
                color: "text.primary",
                ["& .MuiSvgIcon-root"]: {
                  opacity: 0.3,
                },
                ["&.Mui-selected"]: {
                  color: "secondary.dark",
                },
              }),
            ]}
            {...LinkProps(action.url)}
          />
        ))}
      </BottomNavigation>
    </BNBox>
  );
};

export default BNBottomNavigation;
